import { Suspense, lazy, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getConnection } from "../net/connection.js";
import { useTheme } from "../game/theme/useTheme.js";
import { selectConfig, useRoundStore } from "../state/roundStore.js";
import { useConnectionStore } from "../state/connectionStore.js";
import { ToastStack, useGameFeedback } from "../features/notifications/index.js";
import { GamePage } from "../features/game/index.js";
import { Link, ROUTES, useRoute } from "./router/index.js";
import { AdminLayout } from "./AdminLayout.js";
import { TopBar } from "./TopBar.js";

/** Used until the server announces its active theme in `state:sync`. */
const FALLBACK_THEME_SLUG = "classic-red";

/** Loaded on demand: the verifier is not on the critical path of the game. */
const ProvablyFairPage = lazy(() =>
  import("../features/provably-fair/index.js").then((m) => ({ default: m.ProvablyFairPage })),
);

const VehicleGallery = lazy(() =>
  import("../features/theme-preview/index.js").then((module) => ({ default: module.VehicleGallery })),
);
const ThemeAdminPage = lazy(() =>
  import("../features/admin/themes/index.js").then((module) => ({ default: module.ThemeAdminPage })),
);
const VerifyEmailPage = lazy(() =>
  import("../features/auth/index.js").then((module) => ({ default: module.VerifyEmailPage })),
);
const ResetPasswordPage = lazy(() =>
  import("../features/auth/index.js").then((module) => ({ default: module.ResetPasswordPage })),
);
const WalletPage = lazy(() =>
  import("../features/wallet/index.js").then((module) => ({ default: module.WalletPage })),
);
const AuditPage = lazy(() =>
  import("../features/admin/audit/index.js").then((module) => ({ default: module.AuditPage })),
);

/**
 * Header, socket lifecycle and theme application are shared by every route;
 * the route only decides what fills <main>.
 */
const GameSettingsPage = lazy(() =>
  import("../features/admin/settings/index.js").then((module) => ({ default: module.GameSettingsPage })),
);

export function AppShell() {
  const { t } = useTranslation();
  const config = useRoundStore(selectConfig);
  const status = useConnectionStore((s) => s.status);
  const route = useRoute();
  const { theme, error: themeError } = useTheme(config?.activeThemeSlug ?? FALLBACK_THEME_SLUG);

  useGameFeedback(theme);

  useEffect(() => {
    const connection = getConnection();
    connection.connect();
    return () => {
      connection.disconnect();
    };
  }, []);

  const offline = status === "disconnected";

  const isAdmin =
    route === "adminAudit" ||
    route === "adminThemes" ||
    route === "adminVehicles" ||
    route === "adminSettings";
  const content =
    route === "verifyEmail" ? (
      <Suspense fallback={<p role="status">{t("app.loading")}</p>}>
        <VerifyEmailPage />
      </Suspense>
    ) : route === "resetPassword" ? (
      <Suspense fallback={<p role="status">{t("app.loading")}</p>}>
        <ResetPasswordPage />
      </Suspense>
    ) : route === "wallet" ? (
      <Suspense fallback={<p role="status">{t("app.loading")}</p>}>
        <WalletPage />
      </Suspense>
    ) : route === "adminSettings" ? (
      <Suspense fallback={<p role="status">{t("app.loading")}</p>}>
        <GameSettingsPage />
      </Suspense>
    ) : route === "adminAudit" ? (
      <Suspense fallback={<p role="status">{t("app.loading")}</p>}>
        <AuditPage />
      </Suspense>
    ) : route === "adminThemes" ? (
      <Suspense fallback={<p role="status">{t("app.loading")}</p>}>
        <ThemeAdminPage />
      </Suspense>
    ) : route === "provablyFair" ? (
      <Suspense fallback={<p style={{ color: "var(--cx-color-muted)" }}>{t("app.loading")}</p>}>
        <ProvablyFairPage />
      </Suspense>
    ) : route === "howToPlay" ? (
      <section className="cx-copy-page">
        <h1>{t("howToPlay.title")}</h1>
        <p>{t("howToPlay.intro")}</p>
      </section>
    ) : route === "adminVehicles" && theme ? (
      <Suspense fallback={<p role="status">{t("app.loading")}</p>}>
        <VehicleGallery key={theme.slug + theme.version} theme={theme} />
      </Suspense>
    ) : route === "adminVehicles" ? (
      <p role="status">{t("app.loading")}</p>
    ) : route === "game" ? (
      <GamePage theme={theme} />
    ) : (
      <p role="status" style={{ color: "var(--cx-color-muted)" }}>
        {t("nav.notFound")} <Link to={ROUTES.game}>{t("nav.game")}</Link>
      </p>
    );

  return (
    <div className="cx-app-shell">
      <TopBar />

      {offline ? (
        <p
          role="alert"
          className="rounded-md p-3 text-sm"
          style={{ background: "var(--cx-color-surface)", color: "var(--cx-color-warning)" }}
        >
          {t("connection.offline")}
        </p>
      ) : null}

      {themeError !== null ? (
        <p className="text-xs" style={{ color: "var(--cx-color-warning)" }}>
          {themeError}
        </p>
      ) : null}

      <main className={route === "game" ? "cx-player-main" : "cx-page-main"}>
        {isAdmin ? <AdminLayout>{content}</AdminLayout> : content}
      </main>
      <ToastStack />
    </div>
  );
}
